import { CommonModule, DatePipe } from '@angular/common';
import { AuditContextService } from '../../../../../audit-logs/audit-context.service';
import {
  GraphFilterViewTypeOptions,
  GraphFilterTimeRangeOptions,
} from '../../../insights-data/data-graph/graph-filter/graph-filter.enum';
import { Component, OnDestroy } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { CaseBehaviour, CaseDetailModel } from '../../../../insights.interface';
import { finalize, Observable, Subject, take, takeUntil } from 'rxjs';
import { InsightMode } from '../../../../insights.enums';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { ReportFormComponent } from '../report-form/report-form.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  ProgressReportGraphMapping,
  reportDrawerParams,
  ReportPrintData,
  ReportRes,
} from '../report-manager.interface';
import { ReportGraphComponent } from '../report-graph/report-graph.component';
import { ReportManagerService } from '../report-manager.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { GraphList } from '../../../insights-data/insights-data.interface';
import _ from 'lodash';
@Component({
  selector: 'app-create-report',
  standalone: true,
  imports: [
    SvgIconComponent,
    CommonModule,
    NzSpinModule,
    NzButtonModule,
    ReportFormComponent,
    ReportGraphComponent,
  ],
  providers: [DatePipe],
  templateUrl: './create-report.component.html',
})
export class CreateReportComponent implements OnDestroy {
  loading$: Observable<boolean>;
  private readonly destroy$ = new Subject<void>();
  caseParams: reportDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.PRINT_CASE,
    selectedReport: null,
  };
  stepNumber = 1;
  caseDetail: CaseDetailModel = {} as CaseDetailModel;
  reportForm = {
    formValue: {} as ReportRes,
    isFormValid: false,
  };
  reportData: ReportRes | null | undefined = null;
  graphData: ProgressReportGraphMapping[] = [];
  printData: ReportPrintData[] = [];
  constructor(
    private readonly loaderService: LoaderService,
    private readonly drawerService: DrawerService,
    private readonly reportService: ReportManagerService,
    private readonly commonService: CommonService,
    private readonly datePipe: DatePipe,
    private readonly auditContextService: AuditContextService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<reportDrawerParams>(DRAWER_IDS.CREATE_REPORT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.caseDetail = params.case;
          this.reportData = params.selectedReport;
        }
      });
  }

  get isEditMode() {
    return this.caseParams.mode === InsightMode.EDIT_REPORT;
  }

  formValueChange(event: { formValue: ReportRes; isFormValid: boolean }): void {
    this.reportForm = event;
  }

  handleGraph(event: ProgressReportGraphMapping[]) {
    this.graphData = event;
  }

  onNext() {
    if (this.reportForm.isFormValid) {
      this.stepNumber++;
    }
  }
  onPrev() {
    this.stepNumber--;
    this.reportData = this.reportForm.formValue;
  } // Helper to display progressReportGraphMappings for audit log: behaviorCategoryName - typesName (comma separated) - unitName
  private getProgressReportGraphMappingsDisplay(
    arr: unknown,
    graphList: unknown
  ): string {
    interface GraphType {
      id: number;
      behaviorCategoryName?: string;
      behaviorTypes?: { id: number; name: string; isOther?: boolean }[];
      unitName?: string;
    }
    interface MappingType {
      activityDataUnitId: number;
    }
    let parsedArr = arr;
    if (typeof arr === 'string') {
      try {
        parsedArr = JSON.parse(arr);
      } catch {
        return '';
      }
    }
    if (!Array.isArray(parsedArr) || !Array.isArray(graphList)) return '';
    const result: string[] = [];
    for (const mapping of parsedArr as MappingType[]) {
      const graph = (graphList as GraphType[]).find(
        g => g.id === mapping.activityDataUnitId
      );
      const behaviorCategoryName = graph?.behaviorCategoryName || '';
      let typesName = '';
      if (Array.isArray(graph?.behaviorTypes)) {
        typesName = graph.behaviorTypes.map(t => t.name).join(', ');
      }
      const unitName = graph?.unitName || '';
      const parts = [behaviorCategoryName, typesName, unitName].filter(Boolean);
      result.push(parts.join(' - '));
    }
    return JSON.stringify(result);
  }
  submitReport() {
    const formValue = this.reportForm.formValue;
    formValue.progressReportGraphMappings = this.graphData;
    formValue.caseId = this.caseDetail.id;
    const graphList = formValue.graphList;
    formValue.startDate = this.datePipe.transform(
      formValue.startDate,
      'yyyy-MM-dd'
    )!;
    formValue.endDate = this.datePipe.transform(
      formValue.endDate,
      'yyyy-MM-dd'
    )!;
    const original = (this.reportData || {}) as Record<string, unknown>;
    const excludeFields = ['caseId', 'id', 'graphType', 'graphList'];
    // Label map for audit logging
    const labelMap: Record<string, string> = {
      reportName: 'Report Name',
      startDate: 'Start Date',
      endDate: 'End Date',
      progressReportGraphMappings: 'Graph Data',
      showIntervalFrequency: 'Interval Frequency',
      isTrendLineEnabled: 'Trend Line Enabled',
      viewType: 'View Type',
      selectedTimeOption: 'Time Option',
      comment: 'Comment',
    };

    let modifiedFields = this.auditContextService.buildFieldChanges(
      original,
      formValue as unknown as Record<string, unknown>,
      {
        excludeFields: excludeFields,
        sensitiveFields: [],
        labelMap,
      }
    );

    // Map viewType and selectedTimeOption fields to label for audit log
    modifiedFields = modifiedFields.map(field => {
      if (field.field === 'viewType') {
        const getLabel = (id: unknown): string => {
          if (typeof id === 'number' || typeof id === 'string') {
            const found = GraphFilterViewTypeOptions.find(
              opt => String(opt.value) === String(id)
            );
            return found ? found.label : String(id);
          }
          return '';
        };
        return {
          ...field,
          oldValue: getLabel(field.oldValue),
          newValue: getLabel(field.newValue),
        };
      } else if (field.field === 'selectedTimeOption') {
        const getLabel = (id: unknown): string => {
          if (typeof id === 'number' || typeof id === 'string') {
            const found = GraphFilterTimeRangeOptions.find(
              opt => String(opt.value) === String(id)
            );
            return found ? found.label : String(id);
          }
          return '';
        };
        return {
          ...field,
          oldValue: getLabel(field.oldValue),
          newValue: getLabel(field.newValue),
        };
      } else if (field.field === 'progressReportGraphMappings') {
        return {
          ...field,
          oldValue: this.getProgressReportGraphMappingsDisplay(
            field.oldValue ?? [],
            formValue.graphList ?? []
          ),
          newValue: this.getProgressReportGraphMappingsDisplay(
            field.newValue ?? [],
            formValue.graphList ?? []
          ),
        };
      }
      return field;
    });

    const payload = {
      ...formValue,
      _modifiedFields: modifiedFields,
    };
    this.loaderService.show();
    const operation$ = this.reportService.createReport(payload);
    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.generatePrintData(
            graphList ?? [],
            formValue.progressReportGraphMappings,
            this.caseDetail.caseBehaviors
          );
          this.closeDrawer(true);
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  generatePrintData(
    graphList: GraphList[],
    progressReportGraphMappings: ProgressReportGraphMapping[],
    caseBehaviors: CaseBehaviour[]
  ): boolean {
    // Group by caseBehaviorId
    const grouped = _.groupBy(graphList, 'caseBehaviorId');
    const result = Object.keys(grouped).map(behaviorId => {
      const graphs = grouped[behaviorId];
      const firstGraph = graphs[0];
      const matchingBehavior = caseBehaviors.find(
        behavior => behavior.id === firstGraph?.caseBehaviorId
      );

      const graphDetails = graphs.map(g => {
        const gWithType = g as unknown as { graphType?: number };
        const graphType =
          Object.hasOwn(gWithType, 'graphType') &&
          typeof gWithType.graphType === 'number'
            ? gWithType.graphType
            : null;
        return {
          graphType,
          graphImage: g.graphImage || [],
          graphTotalImage: g.graphImageTotal || [],
          commentsData: g.commentsData || [],
        };
      });

      return {
        category: firstGraph?.behaviorCategoryName || 'Unknown',
        behavior:
          firstGraph?.behaviorTypes.map(bt => bt.name).join(', ') || 'Unknown',
        operationalDefinition:
          matchingBehavior?.operationalDefinition || 'No Data',
        goals: matchingBehavior?.goals || 'No Goals',
        graphs: graphDetails,
      };
    });
    this.printData = result;
    return true;
  }

  closeDrawer(isSuccess = false) {
    const printData = {
      reportData: this.reportForm.formValue,
      printData: this.printData,
      isEditMode: this.isEditMode,
    };
    const drawerRef = this.drawerService.getDrawerRef(DRAWER_IDS.CREATE_REPORT);
    drawerRef?.close({ isSuccess, printData: isSuccess ? printData : null });
    if (this.isEditMode) {
      const drawerRef = this.drawerService.getDrawerRef(DRAWER_IDS.EDIT_REPORT);
      drawerRef?.close({ isSuccess, printData: isSuccess ? printData : null });
    }
    const reportManagerDrawerRef = this.drawerService.getDrawerRef(
      DRAWER_IDS.REPORT_MANAGER
    );
    if (isSuccess) {
      reportManagerDrawerRef?.close({
        isSuccess,
        printData: isSuccess ? printData : null,
      });
    }
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_REPORT);
  }
  ngOnDestroy(): void {
    this.loaderService.hide();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
